import { memo, useState, useEffect, useRef } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useStore } from 'store/StoreContext';
import { callIssueConnectTokenApi } from 'requests/RequestToken';
import {
	GetLoginDisplayItemResData,
	callGetLoginDisplayItemApi,
	CommonLoginV1ResData,
	CommonLoginV2ResData,
	callCommonLoginV1AfterAgreeApi,
	callCommonLoginV2AfterAgreeApi,
	TermsRedirectResData,
} from 'requests/RequestLogin';
import { RoutePath } from 'consts/RoutePath';
import { CircularProgress } from '@material-ui/core';
import {
	convertObjectToGetParameter,
	convertObjectToGetParameterWithEncode,
} from 'common/ObjectToGetParameterConverter';
import { HttpStatus } from 'consts/HttpStatus';
import { unescapeHtml } from 'common/HtmlEscaper';
import { ReactError } from 'consts/ReactError';

type Props = {};

export const TermsCompleteTemplate = memo((_props: Props) => {
	const [store] = useStore();
	const history = useHistory();
	const search = useLocation().search;
	const queryParams = new URLSearchParams(search);
	const fromType = queryParams.get('from'); // 遷移元種別
	const connectKiyakuData = store.connectKiyakuData;
	const [ctdata, setCtdata] = useState('');
	const authorizeV1Data = store.authorizeV1Data;
	const [commonLoginV1ResData, setCommonLoginV1ResData] = useState<CommonLoginV1ResData>(undefined);
	const authorizeV2Data = store.authorizeV2Data;
	const [commonLoginV2ResData, setCommonLoginV2ResData] = useState<CommonLoginV2ResData>(undefined);
	const [actionError, setActionError] = useState('');
	const isActionError = actionError !== '';

	const [displayItem, setDisplayItem] = useState<GetLoginDisplayItemResData>(undefined);
	const [loading, setLoading] = useState(false);
	useEffect(() => {
		const execute = async () => {
			if ('othersite' === fromType) {
				setLoading(true);
				const result = await callIssueConnectTokenApi(connectKiyakuData.scd, connectKiyakuData.burl);
				if (result.status !== HttpStatus.OK) {
					setActionError(ReactError.E9999);
					history.push(RoutePath.ERROR);
					return;
				}
				setCtdata(result.data.connecttoken);
				setLoading(false);
			} else if ('commonloginv1' === fromType) {
				setLoading(true);
				// commonloginv1の続きからのAPIを叩く
				const result = await callCommonLoginV1AfterAgreeApi(authorizeV1Data);
				if (result.status !== HttpStatus.OK) {
					setActionError(ReactError.E9999);
					history.push(RoutePath.ERROR);
					return;
				}
				setCommonLoginV1ResData(result.data);
				setLoading(false);
			} else if ('commonloginv2' === fromType) {
				setLoading(true);
				const getDisplayItemResult = await callGetLoginDisplayItemApi({
					gid_dp: authorizeV2Data.gid_dp,
					site_cd: authorizeV2Data.client_id,
				});
				if (getDisplayItemResult.status !== HttpStatus.OK || !getDisplayItemResult.data) {
					setActionError(ReactError.E0008);
					history.push(RoutePath.ERROR);
					return;
				}
				setDisplayItem(getDisplayItemResult.data);
				// commonloginv2の続きからのAPIを叩く
				const result = await callCommonLoginV2AfterAgreeApi(authorizeV2Data);
				if (result.status === HttpStatus.REDIRECT) {
					const termsData = result.data as TermsRedirectResData;
					const getParameters = convertObjectToGetParameterWithEncode(
						store.authorizeV2Data,
						'code_challenge',
						'redirect_uri',
						'nonce',
					);
					if (termsData.type === 'required_profile') {
						history.push(`${RoutePath.REQUIRED_PROFILE_EDIT}?${getParameters}`);
					} else if (termsData.type === 'attribute') {
						history.push(`${RoutePath.ATTRIBUTE_AGREEMENT}?${getParameters}`);
					}
					return;
				}
				if (result.status !== HttpStatus.OK) {
					setActionError(ReactError.E9999);
					history.push(RoutePath.ERROR);
					return;
				}
				const resultData = result.data as CommonLoginV2ResData;
				setCommonLoginV2ResData(resultData);
				setLoading(false);
			}
		};
		execute();
	}, [fromType]);

	// 画面遷移する
	const [returnTransitioning, setReturnTransitioning] = useState(false);
	const trans = () => {
		setReturnTransitioning(true);
		if ('othersite' === fromType) {
			window.location.href = `${decodeURIComponent(connectKiyakuData.burl)}?ctdata=${ctdata}&scd=${
				connectKiyakuData.scd
			}`;
		} else if ('commonloginv1' === fromType) {
			const getParameters = convertObjectToGetParameter(commonLoginV1ResData);
			window.location.href = `${decodeURIComponent(authorizeV1Data.burl)}?${getParameters}`;
		} else if ('commonloginv2' === fromType) {
			const getParameters = convertObjectToGetParameter(commonLoginV2ResData);
			window.location.href = `${
				authorizeV2Data.redirect_uri ? decodeURIComponent(authorizeV2Data.redirect_uri) : displayItem.redirect_url
			}?${getParameters}`;
		} else if ('regchild' === fromType) {
			history.push(RoutePath.MEMBER_ADD);
		} else {
			history.push(RoutePath.MY_PAGE);
		}
	};

	const scrollRef = useRef(null);
	useEffect(() => {
		scrollRef.current.scrollIntoView();
		document.title = 'GakkenID規約同意登録完了';
	}, []);

	const errorRef = useRef(null);
	useEffect(() => {
		if (isActionError) {
			errorRef.current.scrollIntoView();
		}
	}, [actionError]);

	return (
		<main>
			<div className="section">
				<div className="section-in" ref={scrollRef}>
					<div className="section-head">規約同意</div>

					{isActionError ? (
						<div className="error-box" ref={errorRef}>
							<p>{actionError}</p>
						</div>
					) : null}

					<div className="section-txt-box">
						<div className="txt-head">同意登録完了</div>
						<p>同意登録が完了しました。</p>
					</div>

					<div className="btn-box">
						<div className="btn gr">
							{loading || returnTransitioning ? (
								<CircularProgress />
							) : (
								<>
									{'othersite' === fromType ? (
										<button onClick={trans} dangerouslySetInnerHTML={{ __html: unescapeHtml(connectKiyakuData.bsn) }} />
									) : null}
									{'commonloginv1' === fromType ? (
										<button onClick={trans} dangerouslySetInnerHTML={{ __html: unescapeHtml(authorizeV1Data.bsn) }} />
									) : null}
									{'commonloginv2' === fromType ? (
										<input
											type="button"
											onClick={trans}
											value={displayItem?.terms_agree_complete ? displayItem?.terms_agree_complete : '次へ'}
										/>
									) : null}
									{'regchild' === fromType ? (
										<input
											type="button"
											onClick={trans}
											value={displayItem?.terms_agree_complete ? displayItem?.terms_agree_complete : '次へ'}
										/>
									) : null}
									{'login' === fromType ? <input type="button" onClick={trans} value="マイページトップへ" /> : null}
								</>
							)}
						</div>
					</div>
				</div>
			</div>
		</main>
	);
});
